<template>
	<QuestionCard
		title="What data should be included?"
		description="Choose whether to include existing historical data or only send new data going forward."
	>
		<v-row
			class="incremental-mode-row"
			align="stretch"
		>
			<v-col
				cols="6"
				sm="12"
				md="6"
				class="selector-card-col"
			>
				<SelectorCard
					class="equal-height-card"
					:model-value="resetIncrementalValue === false"
					title="Include historical data"
					:description="includeHistoricalDescription"
					@input="handleResetIncrementalNo"
				>
					<template #icon>
						<v-icon
							color="primary"
							size="24"
						>
							mdi-database-export
						</v-icon>
					</template>
				</SelectorCard>
			</v-col>
			<v-col
				cols="6"
				sm="12"
				md="6"
				class="selector-card-col"
			>
				<SelectorCard
					class="equal-height-card"
					:model-value="resetIncrementalValue === true"
					title="Current data only"
					:description="currentOnlyDescription"
					@input="handleResetIncrementalYes"
				>
					<template #icon>
						<v-icon
							color="primary"
							size="24"
						>
							mdi-clock-fast
						</v-icon>
					</template>
				</SelectorCard>
			</v-col>
		</v-row>
	</QuestionCard>
</template>

<script>
import { QuestionCard, SelectorCard } from '../../../../../components/component-library-wrapper.js'

export default {
	name: 'incremental-mode-selector',
	components: {
		QuestionCard,
		SelectorCard
	},
	props: {
		// Whether this is a brand new integration (no persisted service yet)
		isNewIntegration: {
			type: Boolean,
			default: false
		},
		// Whether the integration service is currently enabled
		integrationServiceEnabled: {
			type: Boolean,
			default: false // default to false
		},
		selectedDataTypeName: {
			type: String,
			default: ''
		},
		resetIncrementalValue: {
			type: Boolean,
			default: null
		},
		objectIdColumn: {
			type: String,
			default: null
		},
		incrementFieldItems: {
			type: Array,
			default: () => []
		},
		userFullPermissions: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		includeHistoricalDescription () {
			// New integration-specific messaging
			if (this.isNewIntegration) {
				return `Send all existing eligible ${this.selectedDataTypeName} records, then continue with ongoing changes.`
			}
			// Disabled service-specific messaging
			if (!this.integrationServiceEnabled) {
				return `Send all eligible ${this.selectedDataTypeName} records since the service was last deactivated, then continue with new changes.`
			}
			// Default (existing enabled service)
			return `Send all existing ${this.selectedDataTypeName} data first, then ongoing changes`
		},
		currentOnlyDescription () {
			// New integration-specific messaging
			if (this.isNewIntegration) {
				return `Send only newly eligible ${this.selectedDataTypeName} records from the point of activation onward.`
			}
			// Disabled service-specific messaging
			if (!this.integrationServiceEnabled) {
				return `Send only newly eligible ${this.selectedDataTypeName} records from reactivation onward`
			}
			// Default (existing enabled service)
			return `Only send new or updated ${this.selectedDataTypeName} data from activation forward`
		}
	},
	methods: {
		handleResetIncrementalNo () {
			// Allow users to select data inclusion options regardless of permissions
			// The actual save operation will be controlled by permissions elsewhere
			this.$emit('update:resetIncrementalValue', false)
		},
		handleResetIncrementalYes () {
			// Allow users to select data inclusion options regardless of permissions
			// The actual save operation will be controlled by permissions elsewhere
			this.$emit('update:resetIncrementalValue', true)
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../shared/stepper-styles.scss';

/* Incremental Options */
.incremental-options {
	display: flex;
	gap: 16px;
	justify-content: flex-start;
	max-width: 800px;
	margin-top: 0;
	flex-wrap: wrap;
}

/* Equal height selector cards */
.incremental-mode-row {
	align-items: stretch;
}

.selector-card-col {
	display: flex;
}

/* Assuming SelectorCard root has a class we can target; fall back to direct child > * */
.selector-card-col > .equal-height-card {
	flex: 1 1 auto;
	height: 100%;
	/* Avoid changing internal flex direction so right-side check/tick alignment remains intact */
}
</style>
